import React, {
  createContext, useCallback, useContext, useMemo, useRef, useState,
} from 'react';

import type { WithChildren } from 'common/types';

export type KitView = 'builder' | 'manageKits' | 'myAssemblies';

// Commits the current confirm-page assembly into the workspace if it has not
// been saved yet; returns the workspace key (or null if there is nothing to do).
type PendingCommit = () => Promise<string | null>;

interface IKitViewContext {
  view: KitView;
  openManageKits: () => void;
  closeManageKits: () => void;
  // Switch to the My assemblies review (replaces whatever overlay is open, e.g.
  // Manage Kits, instead of stacking on top of it).
  openMyAssemblies: () => void;
  // The confirm page (KitConfirmActions) registers a committer here so the
  // Manage Kits "Finish" can fold the current, not-yet-saved assembly into the
  // combined PDF. Null when the confirm page is not mounted.
  registerPendingCommit: (fn: PendingCommit | null) => void;
  commitPending: () => Promise<string | null>;
}

const KitViewContext = createContext<IKitViewContext | undefined>(undefined);

/**
 * Minimal view-mode state for the kit journey, lifted to the configurator root
 * so the Manage Kits page can render as an overlay over the wizard without
 * adding react-router. The builder always mounts at view='builder'.
 */
export const KitViewProvider = ({ children }: WithChildren) => {
  const [view, setView] = useState<KitView>('builder');

  const openManageKits = useCallback(() => setView('manageKits'), []);
  const closeManageKits = useCallback(() => setView('builder'), []);
  const openMyAssemblies = useCallback(() => setView('myAssemblies'), []);

  const pendingCommitRef = useRef<PendingCommit | null>(null);
  const registerPendingCommit = useCallback((fn: PendingCommit | null) => {
    pendingCommitRef.current = fn;
  }, []);
  const commitPending = useCallback(
    async () => (pendingCommitRef.current ? pendingCommitRef.current() : null),
    [],
  );

  const value = useMemo<IKitViewContext>(
    () => ({
      view, openManageKits, closeManageKits, openMyAssemblies, registerPendingCommit, commitPending,
    }),
    [view, openManageKits, closeManageKits, openMyAssemblies, registerPendingCommit, commitPending],
  );

  return <KitViewContext.Provider value={value}>{children}</KitViewContext.Provider>;
};

export const useKitView = (): IKitViewContext => {
  const ctx = useContext(KitViewContext);
  if (!ctx) {
    throw new Error('useKitView must be used inside <KitViewProvider>');
  }
  return ctx;
};
